import { DataInput, InputSource } from '@/types/automationFields.ts';
import { Alert, Stack } from '@mantine/core';
import { AutomationStep, ControlBlockingMode, DecisionControlLogic, ValidateStep } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import StyledMultiSelect from '@/components/StyledInputFields/StyledMultiSelect.tsx';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import InputSourceSelectField from '@/pages/Editor/components/SharedFields/InputSourceSelectField.tsx';
import { useMemo } from 'react';
import MultiDataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelectorUI.tsx';
import {
  getMultiLogicDataInputs,
  inputSourceAcceptsDataInput,
  inputSourceAcceptsUserSpecified,
} from '@/pages/Editor/components/SharedFields/utils.ts';
import { STEP_LOGIC_MAX_LENGTH } from '@/utils/constants.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { getLaterSiblingStepOptions } from '@/pages/Editor/components/StepSidebar/StepForms/controlDependencyStepOptions.ts';
import DecisionControlLogicFields from '@/pages/Editor/components/StepSidebar/StepForms/DecisionControlLogicFields.tsx';
import { useActiveCompositionTarget } from '@/layouts/AutomationPageWrapper/hooks/useActiveCompositionTarget.ts';

type props = {
  step: ValidateStep;
};

type Fields = {
  validation_logic_source: InputSource;
  validation_logic_data_inputs: DataInput[];
  validation_instructions: string;
  validation_succeed_logic_description: string;
  validation_fail_logic_description: string;
  control_logic: DecisionControlLogic;
  data_inputs: DataInput[];
  blocking_mode: ControlBlockingMode;
  blocking_step_ids: string[];
};

const ValidateStepFields = ({ step }: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();
  const { target } = useActiveCompositionTarget();
  const isModuleStep = target.scope_kind === 'local_subflow';

  const emptyLogicDescription = useStepSpecificError(step, StepErrorType.EmptyDescription);

  const invalidLogicDataInput = useStepSpecificError(step, StepErrorType.InvalidLogicDataInput);
  const invalidStepGate = useStepSpecificError(step, StepErrorType.InvalidStepGate);
  const invalidProvidedCode = useStepSpecificError(step, StepErrorType.InvalidProvidedCode);

  const blockingStepOptions = useMemo(
    () => {
      const steps = target.scope_kind === 'local_subflow'
        ? latestVersion?.local_subflows?.find((subflow) => subflow.subflow_id === target.subflow_id)?.steps
        : latestVersion?.steps;
      return getLaterSiblingStepOptions((steps ?? []) as AutomationStep[], step.step_id);
    },
    [latestVersion?.local_subflows, latestVersion?.steps, step.step_id, target],
  );

  return (
    <Stack w={'100%'}>
      <InputSourceSelectField
        label="Verification Logic Source"
        value={step.validation_logic_source}
        onChange={(value) => onChangeNoDelay({ validation_logic_source: value })}
      />
      {inputSourceAcceptsUserSpecified(fields.validation_logic_source) && (
        <Stack>
          <StyledTextArea
            label="Instructions"
            value={fields.validation_instructions}
            placeholder="Provide context or instructions on what you want to check and/or how to check it."
            onChange={(event) => {
              onChange({ validation_instructions: event.currentTarget.value });
            }}
            required={false}
            autosize={true}
            minRows={2}
            maxLength={STEP_LOGIC_MAX_LENGTH}
          />

          <StyledTextArea
            label="Describe when this check should succeed"
            value={fields.validation_succeed_logic_description}
            placeholder="Data is correct if..."
            onChange={(event) => {
              onChange({ validation_succeed_logic_description: event.currentTarget.value });
            }}
            required={true}
            autosize={true}
            minRows={2}
            error={emptyLogicDescription}
            maxLength={STEP_LOGIC_MAX_LENGTH}
          />
          <StyledTextArea
            label="Describe when this check should fail"
            value={fields.validation_fail_logic_description}
            placeholder="Data is incorrect if..."
            onChange={(event) => {
              onChange({ validation_fail_logic_description: event.currentTarget.value });
            }}
            required={true}
            autosize={true}
            minRows={2}
            error={emptyLogicDescription}
            maxLength={STEP_LOGIC_MAX_LENGTH}
          />
        </Stack>
      )}
      {inputSourceAcceptsDataInput(fields.validation_logic_source) && (
        <MultiDataInputSelectorUI
          stepId={step.step_id}
          label={'Validation rules or reference data'}
          description={
            'Source material that tells the check how to evaluate the data above; it is not the data being checked.'
          }
          error={invalidLogicDataInput}
          dataInputs={getMultiLogicDataInputs(fields.validation_logic_data_inputs)}
          updateDataInputs={(dataInputs) =>
            onChangeNoDelay({
              validation_logic_data_inputs: dataInputs,
            })
          }
        />
      )}
      {isModuleStep ? (
        <Alert color="gray" title="Protected decision behavior">
          Decision code and downstream gate routing are protected module structure. The Automation Editor keeps the
          ordinary validation fields above editable.
        </Alert>
      ) : (
        <>
          <DecisionControlLogicFields
            controlLogic={fields.control_logic}
            dataInputs={fields.data_inputs}
            invalidProvidedCode={invalidProvidedCode}
            onChange={(control_logic) => onChange({ control_logic })}
            onChangeNoDelay={(control_logic) => onChangeNoDelay({ control_logic })}
          />
          <StyledSelect
            label="Gate behavior"
            placeholder="Select gate behavior"
            data={[
              { value: ControlBlockingMode.None, label: 'Do not gate later steps' },
              { value: ControlBlockingMode.SelectedSteps, label: 'Gate selected later steps' },
              {
                value: ControlBlockingMode.AllLaterStepsInScope,
                label: 'Gate all later steps in this section',
              },
            ]}
            value={fields.blocking_mode ?? ControlBlockingMode.AllLaterStepsInScope}
            onChange={(value) =>
              onChangeNoDelay({
                blocking_mode: value as ControlBlockingMode,
                blocking_step_ids:
                  value === ControlBlockingMode.SelectedSteps ? fields.blocking_step_ids : [],
              })
            }
            error={invalidStepGate}
            required={true}
            allowDeselect={false}
            clearable={false}
          />
          {fields.blocking_mode === ControlBlockingMode.SelectedSteps && (
            <StyledMultiSelect
              label="Steps to gate"
              placeholder="Select later steps"
              data={blockingStepOptions}
              value={fields.blocking_step_ids ?? []}
              onChange={(value) => onChangeNoDelay({ blocking_step_ids: value })}
              error={invalidStepGate}
              searchable={true}
              clearable={true}
            />
          )}
        </>
      )}
    </Stack>
  );
};

export default ValidateStepFields;
